import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Building2, Factory, MapPin, FileDown } from "lucide-react";
import { Reveal, SectionHead, Eyebrow, PageHero, CTASection, usePageMeta } from "../components/ui-bits";
import { IMG, milestones, philosophy, company } from "../data/content";
import { useCmsList } from "../hooks/useCmsContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProfileButton = () => {
  const [p, setP] = useState(null);
  useEffect(() => {
    axios.get(`${API}/company-profile`).then((r) => setP(r.data && r.data.doc_url ? r.data : null)).catch(() => {});
  }, []);
  if (!p) return null;
  return (
    <a href={p.doc_url} target="_blank" rel="noopener noreferrer" data-testid="about-profile-btn"
      className="inline-flex items-center gap-3 border border-[#0A1128] text-[#0A1128] hover:bg-[#0A1128] hover:text-white font-display font-semibold px-7 py-3.5 text-xs uppercase tracking-widest transition-colors duration-300">
      Download Company Profile <FileDown size={15} />
    </a>
  );
};

const About = () => {
  const leaders = useCmsList("leader", company.people);
  usePageMeta(
    "About Us — G. R. Engineering Private Limited | Est. 1966",
    "Incorporated in 1966, G. R. Engineering Private Limited is an established Indian engineering and fabrication organisation with its head office in Mumbai and manufacturing works at Tarapur."
  );
  return (
    <>
      <PageHero
        img={IMG("hmel-loading")}
        eyebrow="About Us"
        title="Six decades of engineering India’s process industry"
        sub="An established engineering and fabrication organisation — incorporated in 1966 — delivering world-class products and services with quality workmanship, competitive costs and on-time delivery."
      />

      <section className="bg-white" data-testid="about-overview">
        <div className="container-gre py-20 lg:py-28 grid lg:grid-cols-2 gap-14 lg:gap-20">
          <div>
            <SectionHead
              eyebrow="Who We Are"
              title="We understand the issues — and come up with solutions"
              sub="G. R. Engineering Private Limited is an established engineering and fabrication organisation serving oil & gas, refinery, petrochemical, fertilizer, chemical and power industries. Every critical aspect of a project is managed by our highly skilled and experienced staff — enabling competitive costs, on-time delivery and high-quality workmanship."
            />
            <Reveal delay={0.2}>
              <div className="mt-9 grid sm:grid-cols-2 gap-5">
                <div className="border border-slate-200 p-6">
                  <Building2 size={22} className="text-[#C5221F]" strokeWidth={1.75} />
                  <p className="mt-4 font-display text-sm font-bold text-slate-900 uppercase tracking-wide">Head Office — Mumbai</p>
                  <p className="mt-2 text-[13px] text-slate-600 leading-relaxed">{company.address.join(", ")} — management, engineering & design, procurement, sales.</p>
                </div>
                <div className="border border-slate-200 p-6">
                  <Factory size={22} className="text-[#C5221F]" strokeWidth={1.75} />
                  <p className="mt-4 font-display text-sm font-bold text-slate-900 uppercase tracking-wide">Works — Tarapur</p>
                  <p className="mt-2 text-[13px] text-slate-600 leading-relaxed">Manufacturing, production, welding technology & NDT, QA/QC, planning and project management.</p>
                </div>
              </div>
            </Reveal>
          </div>
          <div className="relative">
            <Reveal>
              <img src={IMG("hmel-erection")} alt="Column being erected by crawler crane" className="w-full aspect-[3/4] object-cover img-industrial border border-slate-200" />
            </Reveal>
            <Reveal delay={0.15}>
              <div className="absolute -bottom-8 left-0 sm:-left-8 bg-[#0A1128] text-white px-7 py-6 max-w-[260px] shadow-2xl">
                <p className="font-display text-3xl font-black text-[#E8574F]">ASME</p>
                <p className="mt-1 text-xs uppercase tracking-widest font-semibold">'U' &amp; 'U2' Stamp Holder</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 border-y border-slate-200" data-testid="about-history">
        <div className="container-gre py-20 lg:py-28">
          <SectionHead eyebrow="Our Journey" title="From 1966 to mega-scale EPC execution" />
          <div className="mt-14 relative">
            <div className="absolute left-[7px] top-2 bottom-2 w-px bg-slate-300" />
            <div className="space-y-12">
              {milestones.map((m, i) => (
                <Reveal key={m.year} delay={i * 0.08}>
                  <div className="relative pl-12">
                    <span className="absolute left-0 top-1.5 w-[15px] h-[15px] bg-[#C5221F] border-4 border-slate-50" />
                    <p className="font-mono-tech text-sm font-bold text-[#C5221F] tracking-widest">{m.year}</p>
                    <p className="mt-2 text-base text-slate-700 leading-relaxed max-w-2xl">{m.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white" data-testid="about-philosophy">
        <div className="container-gre py-20 lg:py-28 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          <div>
            <SectionHead
              eyebrow="How We Work"
              title="World-class products & services — delivered on our word"
              sub="We continually strive to upgrade our internal processes and skills, and consider our people our biggest assets in fulfilling our commitments to the client."
            />
            <Reveal delay={0.2}>
              <ul className="mt-9 space-y-4">
                {philosophy.map((p) => (
                  <li key={p} className="flex items-start gap-3.5 border-l-2 border-[#C5221F] pl-5 py-1 text-[15px] text-slate-700">{p}</li>
                ))}
              </ul>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <img src={IMG("opal-c4")} alt="C4 hydrogenation unit executed by G. R. Engineering at OPaL Dahej" className="w-full aspect-[16/10] object-cover img-industrial border border-slate-200" />
          </Reveal>
        </div>
      </section>

      <section className="bg-[#0A1128]" data-testid="about-leadership">
        <div className="container-gre py-20 lg:py-28">
          <SectionHead light eyebrow="Leadership" title="Experienced management" />
          <div className="mt-14 grid sm:grid-cols-2 gap-px bg-white/10 border border-white/10 max-w-4xl">
            {leaders.map((p, i) => (
              <Reveal key={p.name} delay={i * 0.08}>
                <div className="bg-[#0A1128] p-9">
                  <div className="w-44 h-[13.2rem] overflow-hidden border border-white/15 bg-white" data-testid={`leader-photo-frame-${i}`}>
                    <img
                      src={IMG(p.img)}
                      alt={`${p.name} — ${p.role}, G. R. Engineering Private Limited`}
                      loading="lazy"
                      data-testid={`leader-photo-${i}`}
                      className="w-full h-full object-cover object-top select-none"
                    />
                  </div>
                  <p className="mt-6 font-display text-xl font-bold text-white">{p.name}</p>
                  <p className="mt-1.5 font-mono-tech text-[11px] uppercase tracking-[0.2em] text-red-400">{p.role}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white border-b border-slate-200">
        <div className="container-gre py-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div className="flex items-start gap-4">
            <MapPin size={22} className="text-[#C5221F] mt-1 shrink-0" />
            <div>
              <p className="font-display text-lg font-bold text-slate-900">Mumbai Head Office · Tarapur Manufacturing Works</p>
              <p className="mt-1 text-sm text-slate-600">Design and management in Worli, Mumbai — heavy fabrication at Tarapur, Maharashtra, with direct highway access and a captive jetty 5 km away.</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-4">
            <Link to="/facilities" data-testid="about-facilities-btn" className="group inline-flex items-center gap-3 bg-[#0A1128] hover:bg-[#C5221F] text-white font-display font-semibold px-7 py-3.5 text-xs uppercase tracking-widest transition-colors duration-300">
              Explore Facilities <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <ProfileButton />
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
};

export default About;
